/**
 * The Publish sheet (plan 3.5, 7.6), as data: one reducer for every state the
 * sheet can be in, and the view models the web dialog and the native form
 * sheet both draw from, so the two cannot drift.
 *
 * States:
 * - `loading`: reading the artifact's site (`GET /api/artifacts/:id/site`).
 * - `form`: not live yet (never published, a draft, or unpublished): the
 *   address field, the search and badge switches, the plan line and Publish.
 * - `publishing`: `POST /api/artifacts/:id/publish` in flight ("Publishing").
 * - `building`: the call answered with the deploy still building (the first
 *   publish can take about 12 s); the sheet waits for the `site:update`
 *   nudge (or the poll) before it says the site is live.
 * - `live`: "Your site is live" with the link, Copy link, Share and Open site.
 * - `manage`: a live site: views, Update site, Change address, the search
 *   switch, Site history and Unpublish.
 * - `rename` / `renaming`: the address field again, with the 30-day redirect
 *   note, and the PATCH in flight.
 * - `history`: the deploys, each ready one with Roll back.
 * - `updating`: Update site or a rollback in flight.
 * - `unpublishing`: `DELETE /api/sites/:id` in flight (after the confirm).
 */
import type {
  AgentArtifactSite,
  AgentArtifactSummary,
  AgentPublishingCapability,
  EntitlementDecision,
  PageFinding,
  PlanTier,
  PublishedSiteDetail,
  PublishedSiteSummary,
  SiteNameUnavailableReason,
} from '@tabbio/types';
import { SITES_POLICY_URL, SITES_POLICY_URL_AR } from '@tabbio/types';
import type { SiteNameCheckState } from './site-name-check';

/**
 * The site an artifact summary carries (`AgentArtifactSummary.site`, filled
 * on every summary since WP12: null when never published), or undefined
 * when the summary has none to give: no summary of this artifact in hand,
 * an API from before the field, or a site that changed after the summary
 * was read (`summaryAt` before `changedAt`).
 */
export function summarySiteFor(
  artifactId: string | null | undefined,
  summary: Pick<AgentArtifactSummary, 'id' | 'site'> | null | undefined,
  summaryAt = 0,
  changedAt = 0,
): AgentArtifactSite | null | undefined {
  if (!artifactId || !summary || summary.id !== artifactId) return undefined;
  if (summary.site === undefined || summaryAt < changedAt) return undefined;
  return summary.site;
}

/** The published sites policy in the reader's language (Arabic, else English). */
export function sitesPolicyUrl(language: string | null | undefined): string {
  return (language ?? '').trim().toLowerCase().startsWith('ar') ? SITES_POLICY_URL_AR : SITES_POLICY_URL;
}

/** Why the sheet opened: publish (or manage when live), manage, share a live site, or rename it. */
export type PublishSheetIntent = 'publish' | 'manage' | 'share' | 'rename';

export type PublishSheetView =
  | 'loading'
  | 'form'
  | 'publishing'
  | 'building'
  | 'live'
  | 'manage'
  | 'rename'
  | 'renaming'
  | 'history'
  | 'updating'
  | 'unpublishing';

export type PublishSheetAction = 'publish' | 'update' | 'rename' | 'rollback' | 'settings';

export type PublishSheetError =
  /** The plan's live sites are used up (`site_limit_reached`). */
  | { kind: 'limit'; message: string; decision: EntitlementDecision | null }
  /** Deploys of the day are used up (`deploy_quota_reached`). */
  | { kind: 'quota'; message: string; decision: EntitlementDecision | null }
  /** The name was taken meanwhile, or broke a rule the field did not catch. */
  | {
      kind: 'name';
      reason: SiteNameUnavailableReason;
      message: string;
      suggestions: string[];
    }
  /** The version has blocking findings (`SITE_CHECKS_FAILED`). */
  | { kind: 'checks'; findings: PageFinding[] }
  /** Publishing is off for this account now (`SITES_DISABLED`). */
  | { kind: 'disabled'; message: string }
  /** Anything else: "Publishing failed. Try again." */
  | { kind: 'failed'; message: string };

export type PublishSheetState = {
  view: PublishSheetView;
  intent: PublishSheetIntent;
  site: PublishedSiteDetail | null;
  error: PublishSheetError | null;
  /** The deploy the `building` view waits for. */
  pendingDeployId: string | null;
  /** What started the call in flight (or the build being waited for). */
  pendingAction: PublishSheetAction | null;
};

export type PublishSheetEvent =
  | { type: 'loaded'; site: PublishedSiteDetail | null }
  | { type: 'submit'; action: PublishSheetAction }
  | { type: 'succeeded'; site: PublishedSiteDetail }
  | { type: 'failed'; error: PublishSheetError }
  /** A fresher copy of the site (the socket nudge or the poll). */
  | { type: 'refreshed'; site: PublishedSiteDetail | null }
  | { type: 'go'; view: 'form' | 'live' | 'manage' | 'rename' | 'history' }
  | { type: 'unpublish' }
  | { type: 'unpublished'; site: PublishedSiteDetail }
  | { type: 'dismiss-error' };

export function initialPublishSheetState(intent: PublishSheetIntent): PublishSheetState {
  return {
    view: 'loading',
    intent,
    site: null,
    error: null,
    pendingDeployId: null,
    pendingAction: null,
  };
}

export function isSiteLive(site: Pick<PublishedSiteSummary, 'status' | 'name'> | null | undefined) {
  return site?.status === 'live' && Boolean(site.name);
}

/** Where the sheet opens for a site: a live one manages (or shares), anything else is the form. */
function restingView(site: PublishedSiteDetail | null, intent: PublishSheetIntent): PublishSheetView {
  if (!isSiteLive(site)) return 'form';
  if (intent === 'share') return 'live';
  if (intent === 'rename' && site?.status !== 'suspended') return 'rename';
  return 'manage';
}

/** The view a failed call returns to, so the user can correct and try again. */
function viewBefore(action: PublishSheetAction | null): PublishSheetView {
  switch (action) {
    case 'rename':
      return 'rename';
    case 'update':
    case 'rollback':
    case 'settings':
      return 'manage';
    default:
      return 'form';
  }
}

/** The view after a finished call. */
function viewAfter(action: PublishSheetAction | null): PublishSheetView {
  return action === 'publish' ? 'live' : 'manage';
}

function inFlightView(action: PublishSheetAction): PublishSheetView {
  switch (action) {
    case 'publish':
      return 'publishing';
    case 'rename':
      return 'renaming';
    default:
      return 'updating';
  }
}

const BUSY: ReadonlySet<PublishSheetView> = new Set([
  'loading',
  'publishing',
  'renaming',
  'updating',
  'unpublishing',
]);

export function isPublishSheetBusy(state: PublishSheetState): boolean {
  return BUSY.has(state.view);
}

export function publishSheetReducer(
  state: PublishSheetState,
  event: PublishSheetEvent,
): PublishSheetState {
  switch (event.type) {
    case 'loaded':
      return {
        ...state,
        site: event.site,
        view: restingView(event.site, state.intent),
        error: null,
      };
    case 'submit':
      if (isPublishSheetBusy(state) || state.view === 'building') return state;
      return {
        ...state,
        view: inFlightView(event.action),
        pendingAction: event.action,
        error: null,
      };
    case 'succeeded': {
      const deploy = event.site.currentDeploy;
      if (deploy?.status === 'building') {
        return {
          ...state,
          site: event.site,
          view: 'building',
          pendingDeployId: deploy.id,
          error: null,
        };
      }
      return {
        ...state,
        site: event.site,
        view: viewAfter(state.pendingAction),
        pendingAction: null,
        pendingDeployId: null,
        error: null,
      };
    }
    case 'failed':
      return {
        ...state,
        view: viewBefore(state.pendingAction),
        pendingAction: null,
        pendingDeployId: null,
        error: event.error,
      };
    case 'refreshed': {
      const site = event.site;
      if (state.view !== 'building') {
        // A settled view follows the fresher copy; a call in flight keeps its view.
        if (isPublishSheetBusy(state)) return { ...state, site: site ?? state.site };
        if (!site) return state;
        const wasLive = isSiteLive(state.site);
        const nowLive = isSiteLive(site);
        // Unpublished somewhere else (the Sites list, the agent): back to the form.
        if (wasLive && !nowLive && state.view !== 'form') {
          return { ...state, site, view: 'form' };
        }
        return { ...state, site };
      }
      if (!site) return state;
      const deploy = site.currentDeploy;
      const waitingFor = state.pendingDeployId;
      if (deploy && (!waitingFor || deploy.id === waitingFor)) {
        if (deploy.status === 'ready') {
          return {
            ...state,
            site,
            view: viewAfter(state.pendingAction),
            pendingAction: null,
            pendingDeployId: null,
          };
        }
        if (deploy.status === 'failed') {
          return {
            ...state,
            site,
            view: viewBefore(state.pendingAction),
            pendingAction: null,
            pendingDeployId: null,
            error: { kind: 'failed', message: '' },
          };
        }
      }
      // The site still serves the previous deploy while the new one builds;
      // keep the building copy of the deploy until it settles.
      return {
        ...state,
        site: { ...site, currentDeploy: state.site?.currentDeploy ?? site.currentDeploy },
      };
    }
    case 'go':
      if (isPublishSheetBusy(state)) return state;
      // A view that needs a live site falls back to the form without one.
      if (
        (event.view === 'manage' || event.view === 'rename' || event.view === 'history' || event.view === 'live') &&
        !isSiteLive(state.site)
      ) {
        return { ...state, view: 'form', error: null };
      }
      return { ...state, view: event.view, error: null };
    case 'unpublish':
      if (isPublishSheetBusy(state) || !isSiteLive(state.site)) return state;
      return { ...state, view: 'unpublishing', pendingAction: null, error: null };
    case 'unpublished':
      return { ...state, site: event.site, view: 'form', error: null, pendingDeployId: null };
    case 'dismiss-error':
      return state.error ? { ...state, error: null } : state;
  }
}

// ── Errors ────────────────────────────────────────────────────────────────

const NAME_REASONS: readonly SiteNameUnavailableReason[] = [
  'invalid',
  'blocked',
  'reserved',
  'username_protected',
  'taken',
  'held',
];

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readFindings(value: unknown): PageFinding[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const entry = record(item);
    if (!entry || typeof entry.ruleId !== 'string') return [];
    return [
      {
        ruleId: entry.ruleId,
        message: typeof entry.message === 'string' ? entry.message : '',
        ...(typeof entry.locator === 'string' ? { locator: entry.locator } : {}),
      },
    ];
  });
}

/**
 * A failed call (a `SitesApiError`-like value: code, message, details and the
 * plan decision) as the sheet shows it.
 */
export function publishErrorFrom(error: unknown): PublishSheetError {
  const source = record(error) ?? {};
  const code = typeof source.code === 'string' ? source.code : '';
  const message = typeof source.message === 'string' ? source.message : '';
  const details = record(source.details) ?? {};
  const decision = record(source.decision) as EntitlementDecision | null;
  if (code === 'SUBSCRIPTION_ENTITLEMENT_DENIED') {
    const reasonCode = decision?.reasonCode as string | undefined;
    if (reasonCode === 'deploy_quota_reached') return { kind: 'quota', message, decision };
    return { kind: 'limit', message, decision };
  }
  if (code === 'SITE_NAME_UNAVAILABLE' || code === 'SITE_NAME_INVALID') {
    const reason = NAME_REASONS.find((value) => value === details.reason) ?? (code === 'SITE_NAME_INVALID' ? 'invalid' : 'taken');
    const suggestions = Array.isArray(details.suggestions)
      ? details.suggestions.filter((item): item is string => typeof item === 'string').slice(0, 3)
      : [];
    return { kind: 'name', reason, message, suggestions };
  }
  if (code === 'SITE_CHECKS_FAILED') {
    return { kind: 'checks', findings: readFindings(details.findings) };
  }
  if (code === 'SITES_DISABLED') return { kind: 'disabled', message };
  return { kind: 'failed', message };
}

// ── Copy keys ─────────────────────────────────────────────────────────────

/** The address field's status line: its copy key (under `chat.artifacts.publish`). */
export function nameReasonKey(reason: SiteNameUnavailableReason): string {
  switch (reason) {
    case 'taken':
      return 'publish.taken';
    case 'reserved':
      return 'publish.reserved';
    case 'held':
      return 'publish.held';
    case 'blocked':
      return 'publish.blocked';
    case 'username_protected':
      return 'publish.usernameProtected';
    default:
      return 'publish.invalid';
  }
}

export type NameStatusModel =
  | { tone: 'none' }
  | { tone: 'checking'; key: 'publish.checking' }
  | { tone: 'available'; key: 'publish.available' }
  | { tone: 'error'; key: string; suggestions: string[] };

/** What the line under the address field says, and the suggestion chips. */
export function nameStatusModel(
  check: SiteNameCheckState,
  error: PublishSheetError | null,
): NameStatusModel {
  // A name refused by the publish call itself wins over an older check.
  if (error?.kind === 'name') {
    return { tone: 'error', key: nameReasonKey(error.reason), suggestions: error.suggestions };
  }
  switch (check.status) {
    case 'checking':
      return { tone: 'checking', key: 'publish.checking' };
    case 'available':
      return { tone: 'available', key: 'publish.available' };
    case 'unavailable':
      return { tone: 'error', key: nameReasonKey(check.reason), suggestions: check.suggestions };
    case 'error':
      return { tone: 'none' };
    default:
      return { tone: 'none' };
  }
}

// ── Plan state ────────────────────────────────────────────────────────────

export type PlanLineModel = {
  used: number;
  limit: number;
  /** Publishing another site needs a free slot. */
  reached: boolean;
  /** Upgrade when a bigger plan exists; at the top, unpublish another site. */
  action: 'upgrade' | 'unpublishOther' | null;
};

/** The next plan up, or null at the top. */
export function nextPlanTier(tier: PlanTier | null | undefined): 'plus' | 'pro' | null {
  if (tier === 'pro') return null;
  if (tier === 'plus') return 'pro';
  return 'plus';
}

export function planLineModel(input: {
  capability: AgentPublishingCapability | null | undefined;
  site: Pick<PublishedSiteSummary, 'status' | 'name'> | null;
  tier: PlanTier | null | undefined;
}): PlanLineModel | null {
  const capability = input.capability;
  if (!capability || capability.liveSites.limit <= 0) return null;
  const { used, limit } = capability.liveSites;
  // A live site of this artifact already holds its slot (the manage view).
  const reached = !isSiteLive(input.site) && used >= limit;
  const next = nextPlanTier(input.tier);
  return {
    used,
    limit,
    reached,
    action: reached ? (next ? 'upgrade' : 'unpublishOther') : null,
  };
}

export type BadgeRowModel =
  /** Paid plans: a switch, on by default. */
  | { mode: 'toggle' }
  /** Free: the badge always shows; a line says so with Upgrade. */
  | { mode: 'free' };

export function badgeRowModel(capability: AgentPublishingCapability | null | undefined): BadgeRowModel {
  return capability?.canHideBadge ? { mode: 'toggle' } : { mode: 'free' };
}

export type PublishFormModel = {
  status: NameStatusModel;
  plan: PlanLineModel | null;
  badge: BadgeRowModel;
  /** The version has findings: publishing is blocked with Fix with Tabbio. */
  blockedByFindings: boolean;
  findingsCount: number;
  canPublish: boolean;
  /** The failure line above the button (not the name reasons, which sit under the field). */
  failure: PublishSheetError | null;
};

export function publishFormModel(input: {
  state: PublishSheetState;
  check: SiteNameCheckState;
  capability: AgentPublishingCapability | null | undefined;
  tier: PlanTier | null | undefined;
  /** Blocking findings on the head version, known before publishing. */
  headErrors: number;
}): PublishFormModel {
  const { state, check, capability } = input;
  const status = nameStatusModel(check, state.error);
  const plan = planLineModel({ capability, site: state.site, tier: input.tier });
  const checksError = state.error?.kind === 'checks' ? state.error : null;
  const findingsCount = checksError
    ? Math.max(checksError.findings.length, 1)
    : Math.max(input.headErrors, state.site?.checks.errors.length ?? 0);
  const blockedByFindings = findingsCount > 0;
  const nameOk =
    check.status === 'available' ||
    // A draft or unpublished site republishing its own address: the check is idle.
    (check.status === 'idle' && Boolean(check.name) && check.name === state.site?.name);
  const limitError = state.error?.kind === 'limit';
  const canPublish =
    state.view === 'form' &&
    capability?.available !== false &&
    nameOk &&
    !(state.error?.kind === 'name') &&
    !blockedByFindings &&
    !(plan?.reached ?? false) &&
    !limitError;
  const failure =
    state.error && state.error.kind !== 'name' && state.error.kind !== 'checks' ? state.error : null;
  return {
    status,
    plan,
    badge: badgeRowModel(capability),
    blockedByFindings,
    findingsCount,
    canPublish,
    failure,
  };
}

// ── Manage ────────────────────────────────────────────────────────────────

export type ManageModel = {
  url: string;
  host: string;
  views30d: number;
  /** A newer version than the published one: Update site carries a dot. */
  updateAvailable: boolean;
  latestVersionNumber: number;
  /** The address before the last rename, still redirecting. */
  previousHost: string | null;
  indexable: boolean;
  showBadge: boolean;
  suspended: boolean;
};

/** `https://sara-bakery.tabbio.site` → `sara-bakery.tabbio.site`. */
export function siteHost(url: string | null | undefined): string {
  if (!url) return '';
  const match = /^[a-z]+:\/\/([^/?#]+)/i.exec(url.trim());
  return (match ? match[1]! : url).replace(/\/$/, '');
}

export function manageModel(site: PublishedSiteDetail): ManageModel {
  const url = site.url ?? '';
  const host = siteHost(url);
  const previousHost =
    site.previousName && host.includes('.')
      ? `${site.previousName}${host.slice(host.indexOf('.'))}`
      : null;
  return {
    url,
    host,
    views30d: site.views30d,
    updateAvailable: site.hasUnpublishedChanges,
    latestVersionNumber: site.latestVersionNumber,
    previousHost,
    indexable: site.indexable,
    showBadge: site.showBadge,
    suspended: site.status === 'suspended',
  };
}

/** The name the address field starts with: the site's own, else the suggestion. */
export function initialSiteName(input: {
  site: Pick<PublishedSiteSummary, 'name'> | null;
  suggestedName?: string | null;
}): string {
  return input.site?.name ?? input.suggestedName ?? '';
}

/** `navigator.share` on web when present, else copy; native always has its share sheet. */
export function shareMethod(env: { canShare: boolean }): 'share' | 'copy' {
  return env.canShare ? 'share' : 'copy';
}
